import moment, { Moment } from 'moment-timezone';
import { AddonService } from 'types/AddonService';
import { Option } from 'types/Generics';
import { Marketplace } from 'types/Marketplace';
import {
    isOnlineBookingBoardingItem,
    isOnlineBookingDaycareItem,
    isOnlineBookingGroomingItem,
    OnlineBookingItem
} from 'types/OnlineBooking';
import { DAYCARE_DURATION_OPTIONS } from 'types/Service';
import { WaitlistTime } from 'types/Waitlist';
import {
    BusinessHours,
    buildBusinessHours,
    getMarketplaceBusinessHoursForDate,
    timeNumberToMinutes
} from 'utils/marketplaceScheduleUtils';
import { calculateDaycareHours, getServicePriceByPet } from 'utils/serviceUtils';

export const WAITLIST_TIME_INTERVAL_MINUTES = 15;
export const WAITLIST_DATE_FORMAT = 'YYYY-MM-DD';

// "HH:MM" (24h), the format the waitlist API expects
export const formatWaitlistTimeId = (time: WaitlistTime): string =>
    `${String(time.hour).padStart(2, '0')}:${String(time.minute).padStart(2, '0')}`;

export const parseWaitlistTimeId = (id: string): WaitlistTime | null => {
    const [hour, minute] = id.split(':').map(Number);

    if (!Number.isFinite(hour) || !Number.isFinite(minute)) {
        return null;
    }

    return { hour, minute };
};

// "9:15 am"
export const formatWaitlistTimeLabel = (time: WaitlistTime): string => {
    const period = time.hour >= 12 ? 'pm' : 'am';
    const hour12 = time.hour % 12 === 0 ? 12 : time.hour % 12;

    return `${hour12}:${String(time.minute).padStart(2, '0')} ${period}`;
};

const getBusinessHoursMinutes = (businessHours: BusinessHours) => {
    const open = timeNumberToMinutes(businessHours.open);
    const close = timeNumberToMinutes(businessHours.close);

    return open === null || close === null ? null : { open, close };
};

/**
 * Preferred time options for a waitlist request, in 15-minute intervals within the
 * location business hours (closing time excluded). For a specific date the hours of
 * that day are used (special hours included); for ASAP (`null`) the union of the
 * open hours across the week.
 */
export const getWaitlistTimeOptions = (
    marketplace: Marketplace,
    date: Moment | null
): Array<Option<string>> => {
    const businessHours: Array<BusinessHours | null> = date
        ? [getMarketplaceBusinessHoursForDate(marketplace, date)]
        : Object.values(marketplace.schedule).map(range =>
              range ? buildBusinessHours(range.open, range.close) : null
          );

    const minutes = new Set<number>();

    businessHours.forEach(hours => {
        const range = hours ? getBusinessHoursMinutes(hours) : null;

        if (!range) {
            return;
        }

        const start =
            Math.ceil(range.open / WAITLIST_TIME_INTERVAL_MINUTES) * WAITLIST_TIME_INTERVAL_MINUTES;

        for (let minute = start; minute < range.close; minute += WAITLIST_TIME_INTERVAL_MINUTES) {
            minutes.add(minute);
        }
    });

    return Array.from(minutes)
        .sort((a, b) => a - b)
        .map(minute => {
            const time = { hour: Math.floor(minute / 60), minute: minute % 60 };

            return {
                id: formatWaitlistTimeId(time),
                name: formatWaitlistTimeLabel(time)
            };
        });
};

/**
 * Dates a cart item asks for, one `YYYY-MM-DD` per occurrence (`[]` = no dates yet):
 * - grooming / dental: the appointment day.
 * - daycare: each selected day.
 * - boarding: each night, from check-in to the night before check-out.
 *
 * Asking ASAP is the "Requested Date" select, not the absence of dates here.
 */
export const getWaitlistItemDates = (item: OnlineBookingItem): Array<string> => {
    if (isOnlineBookingGroomingItem(item)) {
        return item.date ? [item.date.format(WAITLIST_DATE_FORMAT)] : [];
    }

    if (isOnlineBookingDaycareItem(item)) {
        return (item.days ?? []).map(day => day.date.format(WAITLIST_DATE_FORMAT));
    }

    if (isOnlineBookingBoardingItem(item)) {
        const { startDate, endDate } = item;

        if (!startDate || !endDate) {
            return [];
        }

        const nights: Array<string> = [];

        for (const night = startDate.clone(); night.isBefore(endDate, 'day'); night.add(1, 'day')) {
            nights.push(night.format(WAITLIST_DATE_FORMAT));
        }

        return nights;
    }

    return [];
};

// Days / nights the line is priced for: 1 for ASAP or when no dates were picked.
const getWaitlistItemQuantity = (item: OnlineBookingItem, asap: boolean): number =>
    asap ? 1 : Math.max(1, getWaitlistItemDates(item).length);

/**
 * Informative total of the item's service line, with the same pricing as the cart:
 * boarding nightly price x nights, daycare unit price x days (hourly daycare also
 * x hours when the drop-off / pick-up times are known), grooming a single unit.
 * `asap` prices one occurrence. 0 when the price can't be resolved.
 */
export const getWaitlistItemServiceTotal = (item: OnlineBookingItem, asap = false): number => {
    if (!item.service) {
        return 0;
    }

    const quantity = getWaitlistItemQuantity(item, asap);

    if (isOnlineBookingBoardingItem(item)) {
        return getServicePriceByPet(item.service, item.pet, quantity);
    }

    const price = getServicePriceByPet(item.service, item.pet);

    if (!isOnlineBookingDaycareItem(item)) {
        return price;
    }

    const hours =
        item.service.daycareMetadata?.durationOption === DAYCARE_DURATION_OPTIONS.HOURLY &&
        item.dropOffTime &&
        item.pickUpTime
            ? calculateDaycareHours(item.dropOffTime, item.pickUpTime)
            : 1;

    return price * hours * quantity;
};

// Daycare add-ons go once per day; boarding and grooming add-ons once per line.
export const getWaitlistItemAddonTotal = (
    item: OnlineBookingItem,
    addon: AddonService,
    asap = false
): number =>
    isOnlineBookingDaycareItem(item)
        ? addon.price * getWaitlistItemQuantity(item, asap)
        : addon.price;

export const getWaitlistItemsTotalAmount = (
    items: Array<OnlineBookingItem>,
    asap = false
): number =>
    items.reduce((totalAmount, item) => {
        if (!item.service) {
            return totalAmount;
        }

        return (
            totalAmount +
            getWaitlistItemServiceTotal(item, asap) +
            item.addons.reduce(
                (total, addon) => total + getWaitlistItemAddonTotal(item, addon, asap),
                0
            )
        );
    }, 0);

const MAX_SUMMARY_DAYS = 3;

// "Fri, 28 Aug 2026", how the design shows a single requested date
export const WAITLIST_SINGLE_DATE_FORMAT = 'ddd, D MMM YYYY';

/**
 * Short summary of a set of requested dates, `null` when there are none. A single date is
 * "Fri, 28 Aug 2026"; several share the month until it changes: "Oct 6, 8" or
 * "Oct 30, 31, Nov 1". Boarding, where the dates are the nights and the check-out is the
 * morning after the last one, reads "Oct 5 – Oct 8 · 3 nights".
 */
export const formatWaitlistDates = (dates: Array<string>, isBoarding: boolean): string | null => {
    const days = dates.map(date => moment(date, WAITLIST_DATE_FORMAT));

    if (days.length === 0) {
        return null;
    }

    if (isBoarding) {
        const checkOut = days[days.length - 1].clone().add(1, 'day');
        const nights = `${days.length} ${days.length === 1 ? 'night' : 'nights'}`;

        return `${days[0].format('MMM D')} – ${checkOut.format('MMM D')} · ${nights}`;
    }

    if (days.length === 1) {
        return days[0].format(WAITLIST_SINGLE_DATE_FORMAT);
    }

    const summary = days
        .slice(0, MAX_SUMMARY_DAYS)
        .map((day, index, shown) =>
            index > 0 && day.isSame(shown[index - 1], 'month')
                ? day.format('D')
                : day.format('MMM D')
        )
        .join(', ');
    const remaining = days.length - MAX_SUMMARY_DAYS;

    return remaining > 0 ? `${summary} +${remaining} more` : summary;
};

export const formatWaitlistItemDates = (item: OnlineBookingItem): string | null =>
    formatWaitlistDates(getWaitlistItemDates(item), isOnlineBookingBoardingItem(item));

/**
 * Writes the requested dates back into a cart item, in the shape of its business area.
 * The inverse of `getWaitlistItemDates`: the appointment day (grooming, which drops the
 * time the date invalidates), one entry per day (daycare) or the stay from the first
 * night to the morning after the last one (boarding).
 */
export const applyWaitlistItemDates = (
    item: OnlineBookingItem,
    dates: Array<string>,
    timeZone: string
): OnlineBookingItem => {
    const days = dates.map(date => moment.tz(date, WAITLIST_DATE_FORMAT, timeZone));

    if (isOnlineBookingGroomingItem(item)) {
        return {
            ...item,
            date: days[0],
            dateSelectedByClient: days.length > 0,
            time: undefined
        };
    }

    if (isOnlineBookingDaycareItem(item)) {
        return { ...item, days: days.map(date => ({ date })) };
    }

    if (isOnlineBookingBoardingItem(item)) {
        return {
            ...item,
            startDate: days[0],
            endDate: days.length > 0 ? days[days.length - 1].clone().add(1, 'day') : undefined
        };
    }

    return item;
};

/** The nights of a boarding stay: the check-in up to the day before the check-out. */
export const getWaitlistBoardingNights = (checkIn: Moment, checkOut: Moment): Array<string> => {
    const nights: Array<string> = [];

    for (const night = checkIn.clone(); night.isBefore(checkOut, 'day'); night.add(1, 'day')) {
        nights.push(night.format(WAITLIST_DATE_FORMAT));
    }

    return nights;
};
